import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  RefreshCw, 
  Search,
  Loader2,
  Check,
  EyeOff,
  Eye,
  UserX,
  UserCheck,
  Download,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  User,
  Users,
  Filter
} from 'lucide-react';
import { BulkExcludeDialog, BulkRoomAssignmentDialog } from '../../components/BulkOperationsDialogs';

// Debounce hook for auto-save
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
  
  return debouncedCallback;
};

const TeachersPage = () => {
  const { user } = useAuth();
  const isSchoolAdmin = user?.role === 'school_admin';
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingRooms, setSyncingRooms] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExcluded, setShowExcluded] = useState(false);
  const [savingIds, setSavingIds] = useState(new Set());
  const [savedIds, setSavedIds] = useState(new Set());
  
  // Filter state
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [schoolLevelFilter, setSchoolLevelFilter] = useState('all');
  
  // CSV Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Bulk selection state
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [bulkExcludeDialogOpen, setBulkExcludeDialogOpen] = useState(false);
  const [bulkRoomDialogOpen, setBulkRoomDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teachersRes, roomsRes] = await Promise.all([
        api.get('/teachers'),
        api.get('/rooms')
      ]);
      setTeachers(teachersRes.data);
      setRooms(roomsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/teachers/sync');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync teachers');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncRooms = async () => {
    setSyncingRooms(true);
    try {
      const response = await api.post('/rooms/sync');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync rooms');
    } finally {
      setSyncingRooms(false);
    }
  };

  // Export CSV
  const handleExportCSV = async () => {
    try {
      const response = await api.get('/teachers/export-csv', {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'teachers_conference_urls.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  // Import CSV
  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/teachers/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      
      if (response.data.updated > 0) {
        toast.success(`Updated ${response.data.updated} teacher(s)`);
        fetchData(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
      setImportResult({
        total_rows: 0,
        updated: 0,
        skipped: 0,
        errors: [error.response?.data?.detail || 'Import failed']
      });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Auto-save function
  const saveTeacher = useCallback(async (teacherId, field, value) => {
    setSavingIds(prev => new Set(prev).add(teacherId));
    try {
      await api.patch(`/teachers/${teacherId}`, {
        [field]: value
      });
      setSavedIds(prev => new Set(prev).add(teacherId));
      setTimeout(() => {
        setSavedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(teacherId);
          return newSet;
        });
      }, 1500);
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSavingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(teacherId);
        return newSet;
      });
    }
  }, []);

  const debouncedSave = useDebounce(saveTeacher, 800);

  // Handle inline field change
  const handleFieldChange = (teacherId, field, value) => {
    setTeachers(prev => prev.map(t => 
      t.id === teacherId ? { ...t, [field]: value } : t
    ));
    debouncedSave(teacherId, field, value);
  };

  // Handle room change (immediate save)
  const handleRoomChange = async (teacherId, roomId) => {
    const numericRoomId = roomId === 'none' ? null : parseInt(roomId);
    setTeachers(prev => prev.map(t => 
      t.id === teacherId ? { ...t, room_id: numericRoomId } : t
    ));
    await saveTeacher(teacherId, 'room_id', numericRoomId);
  };

  // Handle checkbox change (immediate save, no debounce)
  const handleCheckboxChange = async (teacherId, checked) => {
    setTeachers(prev => prev.map(t => 
      t.id === teacherId ? { ...t, link_verified: checked } : t
    ));
    await saveTeacher(teacherId, 'link_verified', checked);
  };

  // Handle exclude/include teacher
  const handleExcludeToggle = async (teacherId, exclude) => {
    setTeachers(prev => prev.map(t => 
      t.id === teacherId ? { ...t, is_excluded: exclude } : t
    ));
    await saveTeacher(teacherId, 'is_excluded', exclude);
    toast.success(exclude ? 'Teacher excluded' : 'Teacher included');
  };

  // Filter teachers based on search, excluded status, department, and school level
  const filteredTeachers = teachers.filter(t => {
    // First filter by excluded status
    if (showExcluded) {
      if (!t.is_excluded) return false;
    } else {
      if (t.is_excluded) return false;
    }
    
    // Filter by department
    if (departmentFilter !== 'all' && t.department !== departmentFilter) {
      return false;
    }
    
    // Filter by school level
    if (schoolLevelFilter !== 'all' && t.school_level !== schoolLevelFilter) {
      return false;
    }
    
    // Then filter by search term
    if (searchTerm) {
      return `${t.first_name} ${t.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.school_level?.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  });
  
  // Get unique departments and school levels for filters
  const departments = [...new Set(teachers.map(t => t.department).filter(Boolean))].sort();
  const schoolLevels = [...new Set(teachers.map(t => t.school_level).filter(Boolean))].sort();

  // Count excluded teachers
  const excludedCount = teachers.filter(t => t.is_excluded).length;

  // Get room display text by ID - use description field
  const getRoomDisplay = (roomId) => {
    if (!roomId) return null;
    const room = rooms.find(r => r.room_id === roomId);
    if (!room) return `Room ${roomId}`;
    return room.description || room.name || room.abbreviation || `Room ${roomId}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="teachers-page">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-slate-900">Teachers</h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Manage teacher conference information</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              data-testid="export-csv-btn"
              size="sm"
              className="text-xs sm:text-sm"
            >
              <Download className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Download Teacher Online Conference URLs Template</span>
              <span className="sm:hidden">Export URLs</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setImportDialogOpen(true)}
              data-testid="import-csv-btn"
              size="sm"
              className="text-xs sm:text-sm"
            >
              <Upload className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Import Teacher Online Conference URLs</span>
              <span className="sm:hidden">Import URLs</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleSyncRooms}
              disabled={syncingRooms}
              data-testid="sync-rooms-btn"
              size="sm"
              className="text-xs sm:text-sm"
            >
              <RefreshCw className={`h-4 w-4 mr-1 sm:mr-2 ${syncingRooms ? 'animate-spin' : ''}`} />
              {syncingRooms ? 'Syncing...' : 'Sync Rooms'}
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm"
              onClick={handleSync}
              disabled={syncing}
              data-testid="sync-teachers-btn"
              size="sm"
            >
              <RefreshCw className={`h-4 w-4 mr-1 sm:mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Teachers'}
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search teachers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 text-sm"
                    data-testid="search-teachers"
                  />
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg flex-shrink-0">
                  <Switch
                    id="show-excluded"
                    checked={showExcluded}
                    onCheckedChange={setShowExcluded}
                    data-testid="show-excluded-toggle"
                  />
                  <Label htmlFor="show-excluded" className="cursor-pointer flex items-center gap-2 text-xs sm:text-sm whitespace-nowrap">
                    {showExcluded ? (
                      <>
                        <EyeOff className="h-4 w-4 text-rose-500" />
                        <span>Viewing Excluded ({excludedCount})</span>
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 text-slate-500" />
                        <span>View Excluded ({excludedCount})</span>
                      </>
                    )}
                  </Label>
                </div>
              </div>
              
              {/* Filters row */}
              <div className="flex flex-wrap gap-3">
                {departments.length > 0 && (
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger className="w-[180px] text-sm" data-testid="department-filter">
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                
                {schoolLevels.length > 0 && (
                  <Select value={schoolLevelFilter} onValueChange={setSchoolLevelFilter}>
                    <SelectTrigger className="w-[180px] text-sm" data-testid="school-level-filter">
                      <SelectValue placeholder="All School Levels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All School Levels</SelectItem>
                      {schoolLevels.map((level) => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                
                {(departmentFilter !== 'all' || schoolLevelFilter !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      setDepartmentFilter('all');
                      setSchoolLevelFilter('all');
                    }}
                    className="text-xs text-slate-500 hover:text-slate-700"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2 text-lg sm:text-xl">
              {showExcluded ? (
                <>
                  <UserX className="h-5 w-5 text-rose-500" />
                  Excluded Teachers
                </>
              ) : (
                'Active Teachers'
              )}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {showExcluded 
                ? `${filteredTeachers.length} excluded - Click "Include" to restore`
                : `${filteredTeachers.length} active - Changes auto-save`
              }
              {rooms.length > 0 && !showExcluded && ` · ${rooms.length} rooms`}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 sm:p-6">
            {filteredTeachers.length === 0 ? (
              <div className="text-center py-12 px-4">
                {showExcluded ? (
                  <>
                    <UserCheck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No excluded teachers</p>
                    <Button 
                      variant="outline" 
                      className="mt-4" 
                      onClick={() => setShowExcluded(false)}
                    >
                      View Active Teachers
                    </Button>
                  </>
                ) : (
                  <>
                    <GraduationCap className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No teachers found</p>
                    <Button className="mt-4" onClick={handleSync} disabled={syncing}>
                      Sync from Veracross
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* Bulk Actions Bar */}
                {selectedTeachers.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 p-3 mb-4 mx-2 sm:mx-0 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <span className="text-xs sm:text-sm font-medium text-indigo-700">
                      {selectedTeachers.length} selected
                    </span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setBulkExcludeDialogOpen(true)}
                      className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 text-xs"
                      data-testid="bulk-exclude-btn"
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Exclude/Include
                    </Button>
                    {!showExcluded && rooms.length > 0 && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setBulkRoomDialogOpen(true)}
                        className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 text-xs"
                        data-testid="bulk-room-btn"
                      >
                        <Users className="h-4 w-4 mr-1" />
                        Assign Room
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedTeachers([])}
                      className="text-slate-500"
                    >
                      Clear
                    </Button>
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={selectedTeachers.length === filteredTeachers.length && filteredTeachers.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedTeachers(filteredTeachers.map(t => t.id));
                            } else {
                              setSelectedTeachers([]);
                            }
                          }}
                          data-testid="select-all-teachers"
                        />
                      </TableHead>
                      <TableHead className="w-[180px]">Name</TableHead>
                      <TableHead className="w-[130px]">Department</TableHead>
                      <TableHead className="w-[120px]">School Level</TableHead>
                      {isSchoolAdmin && <TableHead className="w-[130px]">Owner</TableHead>}
                      {!showExcluded && (
                        <>
                          <TableHead className="w-[160px]">In-Person Room</TableHead>
                          <TableHead className="w-[200px]">Conference URL</TableHead>
                          <TableHead className="w-[100px]">Password</TableHead>
                          <TableHead className="w-[70px] text-center">Verified</TableHead>
                        </>
                      )}
                      <TableHead className="w-[100px] text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTeachers.map((teacher) => (
                      <TableRow 
                        key={teacher.id} 
                        data-testid={`teacher-${teacher.id}`}
                        className={`${showExcluded ? 'bg-rose-50/50' : ''} ${selectedTeachers.includes(teacher.id) ? 'bg-indigo-50' : ''}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedTeachers.includes(teacher.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedTeachers(prev => [...prev, teacher.id]);
                              } else {
                                setSelectedTeachers(prev => prev.filter(id => id !== teacher.id));
                              }
                            }}
                            data-testid={`select-teacher-${teacher.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {teacher.preferred_name || teacher.first_name} {teacher.last_name}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {teacher.department || <span className="text-slate-400">-</span>}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {teacher.school_level || <span className="text-slate-400">-</span>}
                        </TableCell>
                        {isSchoolAdmin && (
                          <TableCell>
                            {teacher.owner_name ? (
                              <Badge variant="outline" className="flex items-center gap-1 w-fit">
                                <User className="h-3 w-3" />
                                {teacher.owner_name}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                        )}
                        {!showExcluded && (
                          <>
                            <TableCell>
                              <Select
                                value={teacher.room_id ? String(teacher.room_id) : 'none'}
                                onValueChange={(value) => handleRoomChange(teacher.id, value)}
                              >
                                <SelectTrigger className="h-8 text-sm" data-testid={`teacher-room-${teacher.id}`}>
                                  <SelectValue placeholder="Select room">
                                    {teacher.room_id ? getRoomDisplay(teacher.room_id) : <span className="text-slate-400">No room</span>}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No room</SelectItem>
                                  {rooms.map((room) => (
                                    <SelectItem key={room.room_id} value={String(room.room_id)}>
                                      {room.description || room.name || room.abbreviation || `Room ${room.room_id}`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                value={teacher.online_conference_url || ''}
                                onChange={(e) => handleFieldChange(teacher.id, 'online_conference_url', e.target.value)}
                                placeholder="https://zoom.us/j/..."
                                className="h-8 text-sm"
                                data-testid={`teacher-url-${teacher.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={teacher.conference_password || ''}
                                onChange={(e) => handleFieldChange(teacher.id, 'conference_password', e.target.value)}
                                placeholder="Password"
                                className="h-8 text-sm"
                                data-testid={`teacher-password-${teacher.id}`}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={teacher.link_verified || false}
                                onCheckedChange={(checked) => handleCheckboxChange(teacher.id, checked)}
                                data-testid={`teacher-verified-${teacher.id}`}
                              />
                            </TableCell>
                          </>
                        )}
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            {savingIds.has(teacher.id) && (
                              <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                            )}
                            {savedIds.has(teacher.id) && !savingIds.has(teacher.id) && (
                              <Check className="h-4 w-4 text-emerald-600" />
                            )}
                            {!savingIds.has(teacher.id) && !savedIds.has(teacher.id) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleExcludeToggle(teacher.id, !teacher.is_excluded)}
                                className={teacher.is_excluded ? 'text-emerald-600 hover:text-emerald-700' : 'text-rose-600 hover:text-rose-700'}
                                data-testid={`exclude-teacher-${teacher.id}`}
                              >
                                {teacher.is_excluded ? (
                                  <>
                                    <UserCheck className="h-4 w-4 mr-1" />
                                    Include
                                  </>
                                ) : (
                                  <>
                                    <UserX className="h-4 w-4 mr-1" />
                                    Exclude
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CSV Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
                Import Teacher URLs
              </DialogTitle>
              <DialogDescription>
                Upload a CSV file to bulk update teacher conference URLs and passwords.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-sm">Instructions:</h4>
                <ol className="text-sm text-slate-600 list-decimal list-inside space-y-1">
                  <li>Click "Export CSV" to download the current teacher data</li>
                  <li>Open the CSV in Excel or Google Sheets</li>
                  <li>Fill in the <code className="bg-slate-200 px-1 rounded">online_conference_url</code> and <code className="bg-slate-200 px-1 rounded">online_conference_password</code> columns</li>
                  <li>Save the file and upload it here</li>
                </ol>
                <p className="text-xs text-slate-500 mt-2">
                  Note: The <code className="bg-slate-200 px-1 rounded">name</code> column is for reference only and will not be updated.
                </p>
              </div>

              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                  className="hidden"
                  id="csv-upload"
                  data-testid="csv-file-input"
                />
                <label 
                  htmlFor="csv-upload" 
                  className="cursor-pointer"
                >
                  {importing ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-10 w-10 text-indigo-600 animate-spin mb-2" />
                      <span className="text-slate-600">Importing...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-10 w-10 text-slate-400 mb-2" />
                      <span className="text-slate-600">Click to select CSV file</span>
                      <span className="text-xs text-slate-400 mt-1">or drag and drop</span>
                    </div>
                  )}
                </label>
              </div>

              {importResult && (
                <div className={`rounded-lg p-4 ${importResult.errors.length > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    {importResult.errors.length > 0 ? (
                      <>
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                        Import completed with warnings
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 text-emerald-600" />
                        Import successful
                      </>
                    )}
                  </h4>
                  <div className="text-sm space-y-1">
                    <p>Total rows: <strong>{importResult.total_rows}</strong></p>
                    <p className="text-emerald-700">Updated: <strong>{importResult.updated}</strong></p>
                    {importResult.skipped > 0 && (
                      <p className="text-amber-700">Skipped: <strong>{importResult.skipped}</strong></p>
                    )}
                  </div>
                  {importResult.errors.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <p className="text-xs font-medium text-amber-800 mb-1">Errors:</p>
                      <ul className="text-xs text-amber-700 space-y-0.5">
                        {importResult.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setImportDialogOpen(false);
                setImportResult(null);
              }}>
                {importResult ? 'Close' : 'Cancel'}
              </Button>
              {!importResult && (
                <Button onClick={handleExportCSV} variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export First
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Exclude/Include Dialog */}
        <BulkExcludeDialog
          open={bulkExcludeDialogOpen}
          onOpenChange={setBulkExcludeDialogOpen}
          selectedTeachers={selectedTeachers}
          onSuccess={() => {
            setSelectedTeachers([]);
            fetchData();
          }}
        />

        {/* Bulk Room Assignment Dialog */}
        <BulkRoomAssignmentDialog
          open={bulkRoomDialogOpen}
          onOpenChange={setBulkRoomDialogOpen}
          selectedTeachers={selectedTeachers}
          rooms={rooms}
          onSuccess={() => {
            setSelectedTeachers([]);
            fetchData();
          }}
        />
      </div>
    </DashboardLayout>
  );
};

export default TeachersPage;
